import { Link } from "react-router";
import styles from "./ConsentNotice.module.css";

/**
 * The wording that IS the consent.
 *
 * Two pages take consent — /signup, where continuing carries it through the
 * Cognito redirect, and /welcome, where an account that came in through
 * Cognito's own sign-up form gives it after the fact. They must render ONE
 * source: the text displayed is the whole of what was agreed to, so two copies
 * would be two consents that can drift, and `termsVersion` would then be
 * recording a version of something we could no longer produce.
 *
 * Extracted from Login.tsx verbatim on 2026-08-07. Everything the original
 * comment there says about why this shape is defensible still binds, and is
 * repeated here because this is now where the text lives:
 *
 *   1. CONSPICUOUS — full size, directly under the button, never collapsed,
 *      never behind a "read more".
 *   2. It says plainly WHAT arrives: Kev's board and occasional offers.
 *   3. It promises unsubscribe from any message, at any time. SES's
 *      List-Unsubscribe on the marketing topic mechanises that, so the promise
 *      is kept by infrastructure rather than by intention — which is what makes
 *      a bundled marketing consent defensible rather than merely bundled.
 *   4. `termsVersion` records WHICH wording was shown. Changing anything below
 *      means bumping TERMS_VERSION in content/legal.ts, or the record will
 *      name a document that no longer exists.
 *
 * Under the AU Spam Act the question is what we can prove. What we can prove is
 * that this exact text was displayed, that the user acted after seeing it, and
 * when — with a server timestamp we control.
 */
export function ConsentNotice({ lead }: { lead?: string }) {
  return (
    <div className={styles.disclaimer}>
      <p className={styles.disclaimerLead}>{lead ?? "By continuing you acknowledge and agree:"}</p>
      <ul>
        <li>
          You are <strong>18 or over</strong>, and you have read and agree to KEV&rsquo;s{" "}
          <Link to="/legal/terms">Terms &amp; Conditions</Link> and{" "}
          <Link to="/legal/privacy">Privacy Policy</Link>.
        </li>
        {/* BUNDLED, at Ned's direction, after the separate opt-in tick was put to
            him twice. The unsubscribe promise is not decoration: a bundled
            marketing consent leans on the recipient having been told plainly
            what they get and that they can stop it, which is the clause doing
            the work in the operator versions this copies. */}
        <li>
          To receive Kev&rsquo;s board and occasional offers by email. You can unsubscribe from
          any message, at any time.
        </li>
      </ul>
      {/* The KEV-specific half, and the only part that protects us. Sportsbet
          needs licence disclosures; we need "these are estimates, not tips" —
          the same discipline the persona enforces in every reply, stated once
          at the door. NOT a bullet: it is context, not something the punter is
          agreeing to. */}
      <p>
        KEV is analytics, not a bookmaker. We don&rsquo;t take bets or hold money. Our numbers
        are estimates from historical data, not advice or guarantees.
      </p>
      <p>
        Bet within your means. <Link to="/legal/responsible-gambling">Getting help</Link>
      </p>
    </div>
  );
}
